import { APP_ROUTES, getAuthHeaders } from './config';
import { getOidcManager, OIDC_CONFIG, type OidcReturnState } from '../auth/oidcManager';
import { clearAllListViewSnapshots } from '../utils/listViewSession';
import { clearAllModuleLastPaths } from '../utils/moduleLastPath';
import { jwtDecode } from 'jwt-decode';

const AUTH_STORAGE_KEY = 'sso_auth';
const DEV_TEST_TOKEN = (import.meta as any).env?.DEV ? (import.meta as any).env?.VITE_DEV_TEST_TOKEN : '';

export interface AuthData {
  accessToken: string;
  idToken?: string;
  expiresAt: number;
  userInfo: UserInfo | null;
  returnTo?: string;
}

export interface UserInfo {
  sub: string;
  name?: string;
  email?: string;
}

const readAccessToken = (): string | null => {
  const t = localStorage.getItem('access_token');
  if (t && t.trim()) return t;
  if (DEV_TEST_TOKEN && String(DEV_TEST_TOKEN).trim()) return String(DEV_TEST_TOKEN).trim();
  return null;
};

const getTokenExpMs = (accessToken: string): number | null => {
  try {
    const decoded: any = jwtDecode(accessToken);
    return typeof decoded?.exp === 'number' ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

const buildReturnTo = (pathname?: string, search?: string): string => {
  const p = pathname || window.location.pathname || '/';
  const q = search || window.location.search || '';
  const combined = `${p}${q}`;
  return combined.startsWith('/') ? combined : '/';
};

const safeReturnTo = (returnTo: unknown): string => {
  if (typeof returnTo !== 'string') return '/';
  if (!returnTo.startsWith('/')) return '/';
  if (returnTo.startsWith('//')) return '/';
  if (returnTo.toLowerCase().startsWith(APP_ROUTES.CALLBACK_PATH.toLowerCase())) return '/';
  return returnTo;
};

export const authService = {
  /**
   * 点击“登录”或路由守卫触发时调用：
   * - 若本地已有 access_token（localStorage.token）且未过期：不触发跳转
   * - 否则：发起 OIDC 授权跳转
   */
  initiateLogin: async (opts?: { returnTo?: string }): Promise<void> => {
    if (OIDC_CONFIG.skipSSO) return;
    if (authService.isAuthenticated()) return;
    const returnTo = safeReturnTo(opts?.returnTo || buildReturnTo());
    const mgr = getOidcManager();
    await mgr.signinRedirect({
      state: { returnTo } satisfies OidcReturnState,
    });
  },

  handleCallback: async (): Promise<AuthData> => {
    const mgr = getOidcManager();
    const user = await mgr.signinRedirectCallback();
    const accessToken = user.access_token;
    if (!accessToken) throw new Error('SSO登录失败：未获取到 access_token');

    const decoded: any = jwtDecode(accessToken);
    const expiresAt = typeof decoded?.exp === 'number' ? decoded.exp * 1000 : Date.now() + 3600_000;

    const authData: AuthData = {
      accessToken,
      idToken: user.id_token,
      expiresAt,
      userInfo: decoded
        ? {
            sub: String(decoded.sub || ''),
            name: decoded.name,
            email: decoded.email,
          }
        : null,
      returnTo: (user.state as any)?.returnTo,
    };

    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
    localStorage.setItem('access_token', accessToken);
    return authData;
  },

  logout: async (): Promise<void> => {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    let idToken: string | undefined;
    
    if (stored) {
      try {
        const authData = JSON.parse(stored) as AuthData;
        idToken = authData.idToken;

        try {
          await fetch((import.meta as any).env?.VITE_API_BASE_URL + ((import.meta as any).env?.VITE_AUTH_LOGOUT_PATH || '/auth/logout'), {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
          });
        } catch {
          // ignore
        }
      } catch (e) {
        console.error('Logout API error:', e);
      }
    }

    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem('access_token');
    clearAllListViewSnapshots();
    clearAllModuleLastPaths();

    try {
      const mgr = getOidcManager();
      await mgr.signoutRedirect(idToken ? { id_token_hint: idToken } : undefined);
    } catch {
      window.location.href = '/';
    }
  },

  getAuthData: (): AuthData | null => {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) {
      const token = readAccessToken();
      if (!token) return null;
      const expMs = getTokenExpMs(token);
      if (expMs !== null && expMs < Date.now()) return null;
      return {
        accessToken: token,
        expiresAt: expMs ?? Date.now() + 3600_000,
        userInfo: null,
      };
    }
    try {
      const authData = JSON.parse(stored) as AuthData;
      if (authData.expiresAt < Date.now()) {
        // token 过期，清理本地；是否走登出跳转由上层决定
        localStorage.removeItem(AUTH_STORAGE_KEY);
        localStorage.removeItem('access_token');
        return null;
      }
      return authData;
    } catch {
      return null;
    }
  },

  isAuthenticated: (): boolean => {
    const token = readAccessToken();
    if (!token) return false;
    const expMs = getTokenExpMs(token);
    if (expMs !== null && expMs < Date.now()) return false;
    return true;
  },

  getAccessToken: (): string | null => {
    return readAccessToken();
  },

  getPostLoginPath: (): string => {
    return safeReturnTo(authService.getAuthData()?.returnTo);
  },
};
